import React from 'react';
import styled from 'styled-components';
import { FaBullseye, FaEye, FaGem, FaHandshake, FaUserTie, FaSmile, FaHeart, FaStar, FaShieldAlt } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: #FDBF37;
    border-radius: 2px;
  }

  h1 {
    font-size: 3rem;
    color: #0a1165;
    margin-bottom: 1rem;
    font-weight: 800;
    text-transform: uppercase;

    @media (max-width: 768px) {
      font-size: 2.2rem;
    }
  }
`;

const Section = styled.section`
  margin-bottom: 5rem;
`;

const DescriptionCard = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
  line-height: 1.8;
  color: #4a5568;
  font-size: 1.1rem;
  text-align: justify;

  p {
    margin-bottom: 1.5rem;
  }

  strong {
    color: #0a1165;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    font-size: 1rem;
  }
`;

const MissionVisionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
  border-top: 5px solid #FDBF37;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  h2 {
    display: flex;
    align-items: center;
    gap: 1rem;
    color: #0a1165;
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
    font-weight: 700;

    svg {
      color: #FDBF37;
      font-size: 2.2rem;
    }
  }

  p {
    color: #4a5568;
    line-height: 1.7;
    font-size: 1.05rem;
    text-align: justify;
  }
`;

const ValuesSection = styled.div`
  text-align: center;
  margin-top: 5rem;

  h2 {
    font-size: 2.5rem;
    color: #0a1165;
    margin-bottom: 3rem;
    text-transform: uppercase;
    font-weight: 800;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;

  &:hover {
    background: #0a1165;
    transform: translateY(-5px);
    
    h3 {
      color: white;
    }
    
    .icon-container {
      background: #FDBF37;
      color: #0a1165;
    }
  }

  .icon-container {
    width: 70px;
    height: 70px;
    background: #f8f9fa;
    color: #FDBF37;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    transition: all 0.3s ease;
  }

  h3 {
    font-size: 1.2rem;
    color: #0a1165;
    font-weight: 700;
    margin: 0;
    text-transform: uppercase;
    transition: all 0.3s ease;
  }
`;

const Nosotros: React.FC = () => {
  const values = [
    { name: 'RESPONSABILIDAD', icon: <FaShieldAlt /> },
    { name: 'HONESTIDAD', icon: <FaHeart /> },
    { name: 'PROFESIONALISMO', icon: <FaUserTie /> },
    { name: 'ACTITUD', icon: <FaStar /> },
    { name: 'EMPATÍA', icon: <FaSmile /> },
    { name: 'CONFIANZA', icon: <FaHandshake /> },
  ];

  return (
    <Container>
      <Header>
        <h1>Nosotros</h1>
      </Header>

      <Section>
        <DescriptionCard>
          <p>
            Somos la <strong>Ferretería líder en el mercado ucayalino</strong> con más de <strong>30 años de experiencia</strong> en el rubro. Contamos con una variedad de productos de ferretería, acabados y materiales de construcción.
          </p>
          <p>
            Hoy con nuestro renovado formato de <strong>Homecenter</strong> queremos llevar una propuesta de mejoramiento del hogar que brinde soluciones integrales en la construcción, remodelación y/o decoración de tu nuevo espacio.
          </p>
          <p>
            Queremos compartir que nos sentimos orgullosos de ser <strong>100% amazónicos</strong> y de la relación de confianza y gratitud que mantenemos con nuestros clientes a través de los años, brindándoles siempre el mejor servicio y un asesoramiento profesional y personalizado con la mejor solución para sus obras.
          </p>
          <p>
            La calidad de nuestros productos está avalada por la garantía y confianza que ofrecen nuestras marcas líderes en el sector.
          </p>
        </DescriptionCard>
      </Section>

      <MissionVisionGrid>
        <InfoCard>
          <h2><FaBullseye /> Misión</h2>
          <p>
            Comercializar productos de calidad a precios competitivos y con la mejor asesoría para facilitar la realización de los proyectos de nuestros clientes.
          </p>
        </InfoCard>
        <InfoCard>
          <h2><FaEye /> Visión</h2>
          <p>
            Ser la empresa líder en proyectos de construcción y renovación para el hogar con el fin de mejorar la calidad de vida de las personas.
          </p>
        </InfoCard>
      </MissionVisionGrid>

      <ValuesSection>
        <h2>Nuestros Valores</h2>
        <ValuesGrid>
          {values.map((value, index) => (
            <ValueCard key={index}>
              <div className="icon-container">
                {value.icon}
              </div>
              <h3>{value.name}</h3>
            </ValueCard>
          ))}
        </ValuesGrid>
      </ValuesSection>
    </Container>
  );
};

export default Nosotros;
